/**
 * Lightweight browser EXIF parser for extracting GPS coordinates and capture timestamps
 * Supports JPEG, PNG (eXIf chunk), and WebP (EXIF chunk).
 */

export const extractExifFromBlob = async (blobOrFile) => {
  if (!blobOrFile) return null;
  try {
    const arrayBuffer = await blobOrFile.arrayBuffer();
    const dataView = new DataView(arrayBuffer);

    let tiffOffset = -1;

    // 1. Check for JPEG (0xFFD8)
    if (dataView.byteLength > 4 && dataView.getUint16(0) === 0xFFD8) {
      let offset = 2;
      const length = dataView.byteLength;
      while (offset < length - 4) {
        if (dataView.getUint8(offset) !== 0xFF) break;
        const marker = dataView.getUint8(offset + 1);
        // APP1 marker is 0xE1 (0xFFE1)
        if (marker === 0xE1) {
          const segLength = dataView.getUint16(offset + 2);
          // Check for 'Exif\0\0' (0x457869660000)
          if (
            offset + 10 <= length &&
            dataView.getUint32(offset + 4) === 0x45786966 &&
            dataView.getUint16(offset + 8) === 0x0000
          ) {
            tiffOffset = offset + 10;
            break;
          }
          offset += 2 + segLength;
        } else if ((marker >= 0xE0 && marker <= 0xEF) || marker === 0xFE || marker === 0xDB || marker === 0xC0 || marker === 0xC2 || marker === 0xC4) {
          const segLength = dataView.getUint16(offset + 2);
          offset += 2 + segLength;
        } else {
          offset += 2;
        }
      }
    }

    // 2. Check for PNG (0x89504E47)
    if (tiffOffset === -1 && dataView.byteLength > 8 && dataView.getUint32(0) === 0x89504E47) {
      let offset = 8;
      const length = dataView.byteLength;
      while (offset < length - 8) {
        const chunkLen = dataView.getUint32(offset);
        const chunkType = String.fromCharCode(
          dataView.getUint8(offset + 4),
          dataView.getUint8(offset + 5),
          dataView.getUint8(offset + 6),
          dataView.getUint8(offset + 7)
        );
        if (chunkType === 'eXIf') {
          tiffOffset = offset + 8;
          break;
        }
        offset += 12 + chunkLen;
      }
    }

    // 3. Check for WebP
    if (tiffOffset === -1 && dataView.byteLength > 12) {
      const isRiff = dataView.getUint32(0) === 0x52494646; // RIFF
      const isWebp = dataView.getUint32(8) === 0x57454250; // WEBP
      if (isRiff && isWebp) {
        let offset = 12;
        const length = dataView.byteLength;
        while (offset < length - 8) {
          const chunkType = String.fromCharCode(
            dataView.getUint8(offset),
            dataView.getUint8(offset + 1),
            dataView.getUint8(offset + 2),
            dataView.getUint8(offset + 3)
          );
          const chunkLen = dataView.getUint32(offset + 4, true); // WebP uses little-endian chunk sizes
          if (chunkType === 'EXIF') {
            if (
              offset + 14 <= length &&
              dataView.getUint32(offset + 8) === 0x45786966 &&
              dataView.getUint16(offset + 12) === 0x0000
            ) {
              tiffOffset = offset + 14;
            } else {
              tiffOffset = offset + 8;
            }
            break;
          }
          offset += 8 + chunkLen + (chunkLen % 2);
        }
      }
    }

    if (tiffOffset === -1 || tiffOffset >= dataView.byteLength - 8) {
      return null;
    }

    // Parse TIFF header
    const byteOrderMark = dataView.getUint16(tiffOffset);
    const littleEndian = byteOrderMark === 0x4949; // 'II' vs 'MM' (0x4D4D)
    if (!littleEndian && byteOrderMark !== 0x4D4D) {
      return null;
    }

    const tiffMagic = dataView.getUint16(tiffOffset + 2, littleEndian);
    if (tiffMagic !== 0x002A) {
      return null;
    }

    const firstIFDOffset = dataView.getUint32(tiffOffset + 4, littleEndian);
    if (firstIFDOffset < 8 || tiffOffset + firstIFDOffset >= dataView.byteLength) {
      return null;
    }

    const result = {
      gps_coordinates: null,
      gps_latitude: null,
      gps_longitude: null,
      gps_dms: null,
      gps_altitude: null,
      date_taken: null,
      date: null,
      time: null
    };

    let gpsIFDOffset = -1;
    let exifIFDOffset = -1;
    let gpsDate = null;
    let gpsTime = null;

    // Helper to read values
    const readIFD = (ifdAbsOffset) => {
      if (ifdAbsOffset + 2 > dataView.byteLength) return;
      const numEntries = dataView.getUint16(ifdAbsOffset, littleEndian);
      let entryOffset = ifdAbsOffset + 2;

      for (let i = 0; i < numEntries; i++) {
        if (entryOffset + 12 > dataView.byteLength) break;
        const tag = dataView.getUint16(entryOffset, littleEndian);
        const count = dataView.getUint32(entryOffset + 4, littleEndian);
        const valueOffset = entryOffset + 8;

        if (tag === 0x8825) {
          // GPS Info IFD Pointer
          const relOffset = dataView.getUint32(valueOffset, littleEndian);
          gpsIFDOffset = tiffOffset + relOffset;
        } else if (tag === 0x8769) {
          // Exif IFD Pointer
          const relOffset = dataView.getUint32(valueOffset, littleEndian);
          exifIFDOffset = tiffOffset + relOffset;
        } else if (tag === 0x0132 || tag === 0x9003 || tag === 0x9004) {
          // Date / Time
          const strOffset = count > 4 ? tiffOffset + dataView.getUint32(valueOffset, littleEndian) : valueOffset;
          if (strOffset + count <= dataView.byteLength) {
            let str = '';
            for (let c = 0; c < count - 1; c++) {
              str += String.fromCharCode(dataView.getUint8(strOffset + c));
            }
            const formatted = str.trim().replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
            if (!result.date_taken) result.date_taken = formatted;
          }
        }
        entryOffset += 12;
      }
    };

    // Read IFD0
    readIFD(tiffOffset + firstIFDOffset);

    // Read Exif IFD if present
    if (exifIFDOffset > 0) {
      readIFD(exifIFDOffset);
    }

    // Read GPS IFD if present
    if (gpsIFDOffset > 0 && gpsIFDOffset + 2 <= dataView.byteLength) {
      const numGpsEntries = dataView.getUint16(gpsIFDOffset, littleEndian);
      let entryOffset = gpsIFDOffset + 2;

      let latRef = 'N';
      let lonRef = 'E';
      let latDMS = null;
      let lonDMS = null;
      let altitude = null;
      let altRef = 0;

      const readRational = (valAbsOffset) => {
        if (valAbsOffset + 8 > dataView.byteLength) return 0;
        const num = dataView.getUint32(valAbsOffset, littleEndian);
        const den = dataView.getUint32(valAbsOffset + 4, littleEndian);
        return den !== 0 ? num / den : 0;
      };

      for (let i = 0; i < numGpsEntries; i++) {
        if (entryOffset + 12 > dataView.byteLength) break;
        const tag = dataView.getUint16(entryOffset, littleEndian);
        const count = dataView.getUint32(entryOffset + 4, littleEndian);
        const valueOffset = entryOffset + 8;

        if (tag === 0x0001) {
          // GPSLatitudeRef ('N' or 'S')
          latRef = String.fromCharCode(dataView.getUint8(valueOffset)).toUpperCase();
        } else if (tag === 0x0002) {
          // GPSLatitude (3 Rationals: Deg, Min, Sec)
          const dataPtr = count * 8 > 4 ? tiffOffset + dataView.getUint32(valueOffset, littleEndian) : valueOffset;
          const deg = readRational(dataPtr);
          const min = readRational(dataPtr + 8);
          const sec = readRational(dataPtr + 16);
          latDMS = { deg, min, sec };
        } else if (tag === 0x0003) {
          // GPSLongitudeRef ('E' or 'W')
          lonRef = String.fromCharCode(dataView.getUint8(valueOffset)).toUpperCase();
        } else if (tag === 0x0004) {
          // GPSLongitude (3 Rationals: Deg, Min, Sec)
          const dataPtr = count * 8 > 4 ? tiffOffset + dataView.getUint32(valueOffset, littleEndian) : valueOffset;
          const deg = readRational(dataPtr);
          const min = readRational(dataPtr + 8);
          const sec = readRational(dataPtr + 16);
          lonDMS = { deg, min, sec };
        } else if (tag === 0x0005) {
          // GPSAltitudeRef (0 = Above Sea Level, 1 = Below)
          altRef = dataView.getUint8(valueOffset);
        } else if (tag === 0x0006) {
          // GPSAltitude (1 Rational)
          const dataPtr = tiffOffset + dataView.getUint32(valueOffset, littleEndian);
          altitude = readRational(dataPtr);
        } else if (tag === 0x0007) {
          // GPSTimeStamp (3 Rationals: Hour, Min, Sec in UTC)
          const dataPtr = count * 8 > 4 ? tiffOffset + dataView.getUint32(valueOffset, littleEndian) : valueOffset;
          const h = Math.floor(readRational(dataPtr));
          const m = Math.floor(readRational(dataPtr + 8));
          const s = Math.floor(readRational(dataPtr + 16));
          gpsTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        } else if (tag === 0x001D) {
          // GPSDateStamp (ASCII string: YYYY:MM:DD)
          const strOffset = count > 4 ? tiffOffset + dataView.getUint32(valueOffset, littleEndian) : valueOffset;
          if (strOffset + count <= dataView.byteLength) {
            let str = '';
            for (let c = 0; c < count - 1; c++) {
              str += String.fromCharCode(dataView.getUint8(strOffset + c));
            }
            gpsDate = str.trim().replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
          }
        }
        entryOffset += 12;
      }

      if (latDMS && lonDMS) {
        let decLat = latDMS.deg + latDMS.min / 60 + latDMS.sec / 3600;
        if (latRef === 'S') decLat = -decLat;

        let decLon = lonDMS.deg + lonDMS.min / 60 + lonDMS.sec / 3600;
        if (lonRef === 'W') decLon = -decLon;

        result.gps_latitude = decLat.toFixed(6);
        result.gps_longitude = decLon.toFixed(6);
        result.gps_coordinates = `${decLat.toFixed(6)}, ${decLon.toFixed(6)}`;
        result.gps_dms = `${latDMS.deg}°${Math.floor(latDMS.min)}'${latDMS.sec.toFixed(1)}"${latRef}, ${lonDMS.deg}°${Math.floor(lonDMS.min)}'${lonDMS.sec.toFixed(1)}"${lonRef}`;

        if (altitude !== null) {
          const finalAlt = altRef === 1 ? -altitude : altitude;
          result.gps_altitude = `${finalAlt.toFixed(1)} m`;
        }
      }
    }

    if (!result.date_taken && (gpsDate || gpsTime)) {
      result.date_taken = [gpsDate, gpsTime].filter(Boolean).join(' ');
    }
    if (result.date_taken) {
      const parts = result.date_taken.split(/[\sT]/);
      result.date = parts[0] || null;
      result.time = parts[1] || null;
    } else {
      result.date = gpsDate || null;
      result.time = gpsTime || null;
    }

    return result;
  } catch (err) {
    console.warn('Error reading EXIF data:', err);
    return null;
  }
};

// In-memory cache for reverse geocode lookups to avoid repeat network requests
const reverseGeocodeCache = new Map();

/**
 * Safe reverse geocode helper to resolve human-readable place names from GPS coordinates
 */
export const reverseGeocodePlace = async (lat, lon) => {
  if (lat === null || lat === undefined || lon === null || lon === undefined) return null;
  try {
    const parsedLat = parseFloat(lat);
    const parsedLon = parseFloat(lon);
    if (isNaN(parsedLat) || isNaN(parsedLon)) return null;

    const cacheKey = `${parsedLat.toFixed(4)},${parsedLon.toFixed(4)}`;
    if (reverseGeocodeCache.has(cacheKey)) {
      return reverseGeocodeCache.get(cacheKey);
    }

    let signal;
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 3500);
      signal = controller.signal;
    }

    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(parsedLat)}&lon=${encodeURIComponent(parsedLon)}&zoom=10`,
      { signal }
    );
    if (resp.ok) {
      const data = await resp.json();
      const addr = data.address || {};
      const locality = addr.city || addr.town || addr.village || addr.suburb || addr.municipality || addr.county || '';
      const state = addr.state || addr.province || addr.region || '';
      const country = addr.country || '';
      const parts = [locality, state, country].filter(Boolean);
      const placeName = parts.length > 0 ? parts.join(', ') : (data.display_name?.split(',').slice(0, 3).join(', ') || null);
      if (placeName) {
        reverseGeocodeCache.set(cacheKey, placeName);
      }
      return placeName;
    }
  } catch (err) {
    console.warn('[ReverseGeocode] place lookup error/timeout:', err);
    return null;
  }
  return null;
};
